import {
  _requireAccessLevelAndLocation,
  redirectBusinessRoute,
  requireAuthAndLocation,
} from "../auth";
import {
  conversationAIRouteNames,
  locationSettingsRouteNames,
  opportunitiesRouteNames,
  voiceAIRouteNames,
} from "@/routes/constants/location";
import {
  knowledgeBaseRouteNames,
  reputationRouteNames,
} from "../constants/location";

import { Modules } from "../../util/modules";
import { RouteConfig, Route, RawLocation } from "vue-router";
import { routePaths } from "../paths";
import EmptyRouterParent from "@/pmd/pages/EmptyRouterParent.vue";
import store from "@/store";

const CompanySettingsPage = () =>
  import(
    /* webpackChunkName: "location-company-settings" */ "@/pmd/pages/settings/CompanySettingsPage.vue"
  ).then((m) => m.default);

const LocationBillingPage = () =>
  import(
    /* webpackChunkName: "location-company-settings" */ "@/pmd/pages/LocationBilling.vue"
  ).then((m) => m.default);

const AccountTeamsManagement = () =>
  import(
    /* webpackChunkName: "team-management" */ "@/pmd/pages/agency/AccountTeamsManagement.vue"
  ).then((m) => m.default);

const Team = () => import("@/pmd/pages/agency/Team.vue").then((m) => m.default);

const NewCalendarSetupPage = () =>
  import(
    /* webpackChunkName: "calendar-setup" */ "@/pmd/pages/settings/NewCalendarSetupPage.vue"
  ).then((m) => m.default);

const ServiceSettings = () =>
  import(
    /* webpackChunkName: "service-settings" */ "@/pmd/pages/calendars/services/ServiceSettings.vue"
  ).then((m) => m.default);

const CalendarRentals = () =>
  import(
    /* webpackChunkName: "calendar-rentals" */ "@/pmd/pages/calendars/rentals/CalendarRentals.vue"
  ).then((m) => m.default);

const CalendarConnections = () =>
  import(
    /* webpackChunkName: "calendar-connections" */ "@/pmd/pages/calendars/CalendarConnections.vue"
  ).then((m) => m.default);


const AllPhoneNumbersSettings = () =>
  import(
    /* webpackChunkName: "location-phone-number-settings" */ "@/pmd/pages/settings/AllPhoneNumbersSettings.vue"
  ).then((m) => m.default);

const WhatsApp = () =>
  import(
    /* webpackChunkName: "whatsapp" */ "@/pmd/pages/settings/WhatsAppSettings.vue"
  );

const ISVApp = () => import("@/pmd/pages/isv/ISVApp.vue");

const CustomFieldsRouter = () =>
  import(
    /* webpackChunkName: "custom-fields-settings" */ "@/apps/custom-fields/CustomFieldsRouter.vue"
  ).then((m) => m.default);

const CustomValues = () =>
  import(
    /* webpackChunkName: "location-custom-values" */ "@/pmd/pages/settings/CustomValues.vue"
  ).then((m) => m.default);

const ScoringRouter = () =>
  import(
    /* webpackChunkName: "custom-fields-settings" */ "@/apps/manage-scoring/ManageScoringRouter.vue"
  ).then((m) => m.default);

const PreferenceManagement = () =>
  import(
    /* webpackChunkName: "preference-management-settings" */ "@/pmd/pages/settings/PreferenceManagement.vue"
  ).then((m) => m.default);

const DomainSettings = () =>
  import(
    /* webpackChunkName: "domain-settings" */ "@/pmd/pages/settings/DomainSettings.vue"
  ).then((m) => m.default);

const RedirectSettings = () =>
  import("@/pmd/pages/settings/RedirectSettings.vue").then((m) => m.default);

const ConversationProviderSettings = () =>
  import("@/pmd/pages/settings/ConversationProviderSettings.vue").then(
    (m) => m.default
  );

const ExternalTrackingSettings = () =>
  import("@/pmd/pages/settings/ExternalTrackingSettings.vue").then(
    (m) => m.default
  );

const TagsSettings = () =>
  import(
    /* webpackChunkName: "tags-settings" */ "@/pmd/pages/settings/manage_tags/TagsSettings.vue"
  ).then((m) => m.default);

const GhlLabs = () =>
  import(
    /* webpackChunkName: "domain-settings" */ "@/pmd/pages/settings/GhlLabs/GhlLabs.vue"
  ).then((m) => m.default);

const AuditLogs = () => import("@/pmd/pages/audit/AuditLogs.vue");

const PipelineSettings = () =>
  import(
    /* webpackChunkName: "location-pipeline-settings" */ "@/pmd/pages/settings/PipelineSettings.vue"
  ).then((m) => m.default);

const IntegrationSettingsPage = () =>
  import(
    /* webpackChunkName: "location-integration-settings" */ "@/pmd/pages/settings/IntegrationSettingsPage.vue"
  );

const FacebookAdFieldsMappingSettings = () =>
  import(
    /* webpackChunkName: "form-builder-mapping-settings" */ "@/pmd/pages/settings/FacebookAdFieldsMappingSettings.vue"
  );

const TikTokAdFieldsMappingSettings = () =>
  import(
    /* webpackChunkName: "form-builder-mapping-settings" */ "@/pmd/pages/integrations/TikTokAdFieldsMappingSettings.vue"
  );

const LinkedInFormsPage = () =>
  import(
    /* webpackChunkName: "form-builder-mapping-settings" */ "@/pmd/pages/integrations/linkedIn/LinkedInFormsPage.vue"
  );

const FacebookFormsPage = () =>
  import(
    /* webpackChunkName: "form-builder-mapping-settings" */ "@/pmd/pages/integrations/facebook/FacebookFormsPage.vue"
  );

const PrivateIntegration = () =>
  import("@/pmd/pages/settings/PrivateIntegration.vue").then((m) => m.default);

const SmartList = () =>
  import(
    /* webpackChunkName: "smartlist" */ "@/pmd/pages/smartlist/SmartList.vue"
  ).then((m) => m.default);

const BusinessSettings = () =>
  import(
    /* webpackChunkName: "business-settings" */ "@/pmd/pages/settings/BusinessSettings.vue"
  ).then((m) => m.default);

const CustomEmailsSettings = () =>
  import(
    /* webpackChunkName: "tags-settings" */ "@/pmd/pages/settings/CustomEmailsSettings.vue"
  ).then((m) => m.default);


const GMBPagesV2 = () => import("@/pmd/pages/integrations/gmb/GMBPagesV2.vue");
const LcIntegrationPage = () =>
  import("@/pmd/pages/integrations/lcIntegrations/LcIntegrationPage.vue");

// Route guard to redirect between old and new integration pages based on feature flag
const checkIntegrationsPageRedirect = async (
  to: Route,
  from: Route,
  next: (to?: RawLocation) => void
) => {
  // Track when requireAuthAndLocation completes
  let authCompleted = false;
  let authRedirect: RawLocation | undefined;
  let authResolve: (() => void) | null = null;

  // Create a promise that resolves when requireAuthAndLocation calls next()
  const authPromise = new Promise<void>((resolve) => {
    authResolve = resolve;
  });

  // Wrap next to intercept requireAuthAndLocation's navigation calls
  const wrappedNext = (result?: RawLocation) => {
    if (result && result !== true) {
      // requireAuthAndLocation wants to redirect (auth failed, permission denied, etc.)
      authRedirect = result;
      authCompleted = true;
      if (authResolve) authResolve();
      return;
    }
    // requireAuthAndLocation passed, continue to feature flag check
    authCompleted = true;
    if (authResolve) authResolve();
  };

  // Call requireAuthAndLocation with wrapped next
  requireAuthAndLocation(to, from, wrappedNext);

  // Wait for requireAuthAndLocation to complete
  await authPromise;

  // If requireAuthAndLocation redirected, use that redirect
  if (authRedirect) {
    return next(authRedirect);
  }

  // Now check feature flag and redirect if needed
  const isV2PageEnabled = !!store.getters["featureFlags/getForLocation"](
    "integrations.V2Page"
  );

  if (
    to.name === locationSettingsRouteNames.integrations.list &&
    isV2PageEnabled
  ) {
    return next({
      name: locationSettingsRouteNames.lcIntegrations.default,
      params: to.params,
      query: to.query,
      replace: true,
    });
  }

  if (
    to.name === locationSettingsRouteNames.lcIntegrations.default &&
    !isV2PageEnabled
  ) {
    return next({
      name: locationSettingsRouteNames.integrations.list,
      params: to.params,
      query: to.query,
      replace: true,
    });
  }

  next();
};

const GoogleConnectionTroubleshoot = () =>
  import(
    /* webpackChunkName: "form-builder-mapping-settings" */ "@/pmd/pages/integrations/google/GoogleConnectionTroubleshoot.vue"
  );

export const locationSettingsRoute: RouteConfig[] = [
  {
    path: "company",
    name: locationSettingsRouteNames.company,
    component: CompanySettingsPage,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "company-billing",
    name: locationSettingsRouteNames.locationBilling.default,
    beforeEnter: requireAuthAndLocation,
    component: LocationBillingPage,
    children: [
      {
        path: "billing",
        name: locationSettingsRouteNames.locationBilling.billingV2,
        component: LocationBillingPage,
      },
      {
        path: "transactions",
        name: locationSettingsRouteNames.locationBilling.transactions,
        component: LocationBillingPage,
      },
    ],
  },
  {
    path: "staff",
    name: locationSettingsRouteNames.staff.default,
    component: EmptyRouterParent,
    beforeEnter: _requireAccessLevelAndLocation,
    children: [
      {
        path: "teams",
        name: locationSettingsRouteNames.staff.teams,
        component: AccountTeamsManagement,
        beforeEnter: _requireAccessLevelAndLocation,
        meta: { translations: [Modules.BusinessSettings] },
      },
      {
        path: "team",
        name: locationSettingsRouteNames.staff.team,
        component: Team,
        beforeEnter: _requireAccessLevelAndLocation,
      },
    ],
  },
  {
    path: "calendars",
    component: EmptyRouterParent,
    beforeEnter: requireAuthAndLocation,
    children: [
      {
        path: "",
        name: locationSettingsRouteNames.calendars.default,
        component: NewCalendarSetupPage,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "preferences",
        name: locationSettingsRouteNames.calendars.preferences,
        component: NewCalendarSetupPage,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "availability",
        name: locationSettingsRouteNames.calendars.availability,
        component: NewCalendarSetupPage,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "connections",
        name: locationSettingsRouteNames.calendars.connections,
        component: NewCalendarSetupPage,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "services",
        name: locationSettingsRouteNames.calendars.services,
        component: ServiceSettings,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "rentals",
        name: locationSettingsRouteNames.calendars.rentals,
        component: CalendarRentals,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "connections-new",
        name: locationSettingsRouteNames.calendars.newConnections,
        component: CalendarConnections,
        beforeEnter: requireAuthAndLocation,
      },
    ],
  },
  {
    path: "conversation-ai",
    name: conversationAIRouteNames.conversationAISettings,
    redirect: (to) => ({
      name: conversationAIRouteNames.aiAgentsConversationAi,
      params: { location_id: to.params.location_id },
    }),
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "conversation-ai-v2",
    name: conversationAIRouteNames.conversationAISettingsV2,
    redirect: (to) => ({
      name: conversationAIRouteNames.aiAgentsConversationAi,
      params: { location_id: to.params.location_id },
    }),
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "ai-agents",
    name: voiceAIRouteNames.aiAgentsDefault,
    redirect: (to) => ({
      name: conversationAIRouteNames.aiAgentsVoiceAi,
      params: { location_id: to.params.location_id },
    }),
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "phone_number",
    name: locationSettingsRouteNames.phoneNumber,
    // component: AllPhoneNumbersSettings,
    // beforeEnter: requireAuthAndLocation,
    redirect: { name: locationSettingsRouteNames.phoneSystem },
  },
  {
    path: "phone_system",
    name: locationSettingsRouteNames.phoneSystem,
    component: AllPhoneNumbersSettings,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "whatsapp",
    name: locationSettingsRouteNames.whatsapp,
    component: WhatsApp,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: routePaths.smtpServices,
    name: locationSettingsRouteNames.emailService.default,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: routePaths.emailServicesDedicatedDomains,
    name: locationSettingsRouteNames.emailService.dedicatedDomains.default,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: routePaths.emailServicesAddDomain,
    name: locationSettingsRouteNames.emailService.dedicatedDomains.addDomain,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: routePaths.emailServicesSmtpSettings,
    name: locationSettingsRouteNames.emailService.dedicatedDomains
      .domainSettings,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: routePaths.emailServicesVerifyDomain,
    name: locationSettingsRouteNames.emailService.dedicatedDomains.verifyDomain,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: routePaths.blacklistMonitorReports,
    name: locationSettingsRouteNames.emailService.blacklistMonitorReports,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: routePaths.emailServicesOPMTrafficStats,
    name: locationSettingsRouteNames.emailService.postmasterTools
      .opmTrafficStats,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: routePaths.emailServicesGPMTrafficStats,
    name: locationSettingsRouteNames.emailService.postmasterTools
      .gpmTrafficStats,
    beforeEnter: requireAuthAndLocation,
    component: ISVApp,
  },
  {
    path: "reputation/settings",
    name: locationSettingsRouteNames.reputation,
    redirect: { name: reputationRouteNames.settings },
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "fields",
    name: locationSettingsRouteNames.fields,
    component: CustomFieldsRouter,
    beforeEnter: requireAuthAndLocation,
    meta: { translations: [Modules.CustomField] },
  },
  {
    path: "custom_values",
    name: locationSettingsRouteNames.customValues,
    component: CustomValues,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "scoring",
    name: locationSettingsRouteNames.scoring,
    component: ScoringRouter,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "preferences",
    name: locationSettingsRouteNames.preferences,
    component: PreferenceManagement,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "domain",
    name: locationSettingsRouteNames.domain,
    component: DomainSettings,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "redirect",
    name: locationSettingsRouteNames.URLRedirect,
    component: RedirectSettings,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "external-tracking",
    name: locationSettingsRouteNames.externalTracking,
    component: ExternalTrackingSettings,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "conversation_providers",
    name: locationSettingsRouteNames.conversationProviders,
    component: ConversationProviderSettings,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "tags",
    name: locationSettingsRouteNames.tags,
    component: TagsSettings,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "labs",
    name: locationSettingsRouteNames.labs,
    component: GhlLabs,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "audit/logs",
    name: locationSettingsRouteNames.auditLogs,
    component: AuditLogs,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "private-integrations",
    name: locationSettingsRouteNames.privateIntegrations.default,
    component: PrivateIntegration,
    beforeEnter: requireAuthAndLocation,
    children: [
      {
        path: "",
        name: locationSettingsRouteNames.privateIntegrations.list,
        component: PrivateIntegration,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: ":appId",
        name: locationSettingsRouteNames.privateIntegrations.appId,
        component: PrivateIntegration,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: ":appId/edit",
        name: locationSettingsRouteNames.privateIntegrations.appIdEdit,
        component: PrivateIntegration,
        beforeEnter: requireAuthAndLocation,
      },
    ],
  },
  {
    path: "integrations",
    name: locationSettingsRouteNames.integrations.default,
    component: EmptyRouterParent,
    beforeEnter: requireAuthAndLocation,
    children: [
      {
        path: "list",
        name: locationSettingsRouteNames.integrations.list,
        component: IntegrationSettingsPage,
        beforeEnter: checkIntegrationsPageRedirect,
        meta: { translations: [Modules.BusinessSettings] },
      },
      {
        path: "facebook",
        name: locationSettingsRouteNames.integrations.facebook,
        component: FacebookAdFieldsMappingSettings,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "tiktok",
        name: locationSettingsRouteNames.integrations.tiktok,
        component: TikTokAdFieldsMappingSettings,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "linkedin",
        name: locationSettingsRouteNames.integrations.linkedIn,
        component: LinkedInFormsPage,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "facebook-forms",
        name: locationSettingsRouteNames.integrations.facebookForms,
        component: FacebookFormsPage,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "gbp",
        name: locationSettingsRouteNames.integrations.gbpPages,
        component: GMBPagesV2,
        beforeEnter: requireAuthAndLocation,
      },
      {
        path: "google/connection-issues",
        name: locationSettingsRouteNames.integrations.googleConnectionIssues,
        component: GoogleConnectionTroubleshoot,
        beforeEnter: requireAuthAndLocation,
      },
    ],
  },
  {
    path: "lc-integrations",
    name: locationSettingsRouteNames.lcIntegrations.default,
    component: LcIntegrationPage,
    beforeEnter: checkIntegrationsPageRedirect,
  },
  {
    path: "pipeline",
    name: locationSettingsRouteNames.pipeline,
    component: PipelineSettings,
    beforeEnter: requireAuthAndLocation,
    meta: { translations: [Modules.PipelineSettings] },
  },
  {
    path: "bulk-actions",
    redirect: { name: opportunitiesRouteNames.BulkActions },
  },
  {
    path: "smart_list/Settings",
    name: locationSettingsRouteNames.smartList,
    component: SmartList,
    beforeEnter: requireAuthAndLocation,
    meta: { translations: [Modules.SmartList] },
  },
  {
    path: "businesses",
    name: locationSettingsRouteNames.business,
    component: BusinessSettings,
    beforeEnter: redirectBusinessRoute,
    meta: { translations: [Modules.BusinessSettings] },
  },
  {
    path: "custom_emails",
    name: locationSettingsRouteNames.customEmails,
    component: CustomEmailsSettings,
    beforeEnter: requireAuthAndLocation,
  },
  {
    path: "knowledge-base-settings",
    name: knowledgeBaseRouteNames.knowledgeBaseSettings,
    redirect: (to) => ({
      name: knowledgeBaseRouteNames.aiAgentsKnowledgeBase,
      params: { location_id: to.params.location_id },
    }),
    beforeEnter: requireAuthAndLocation,
  },
  // Added for backward compatibility. To be removed after 2025-09-30
  {
    path: "knowledge-base",
    redirect: "../ai-agents/knowledge-base",
  },
  {
    path: "knowledge-base/:knowledgeBaseId",
    redirect: (to) =>
      `../ai-agents/knowledge-base/${to.params.knowledgeBaseId}`,
  },
  {
    path: "knowledge-base-listing",
    redirect: "../ai-agents/knowledge-base",
  },
  {
    path: "knowledge-base-listing/knowledgeBase/:knowledgeBaseId",
    redirect: (to) =>
      `../ai-agents/knowledge-base/${to.params.knowledgeBaseId}`,
  },
];
